// Admin Devices API layer — typed wrappers over the LIVE /v3/admin/devices contract.
//
// ⚠️ LIVE JSON IS GROUND TRUTH (FACTS PACK §1): the deployed build (commit a33e3b5)
// is ahead of the source checkout; shapes below are probe-confirmed against
// v3-api-dev, not admin.go. All timestamps are MICROSECONDS since epoch.
//
// Depends ONLY on the scaffold's shared surface: apiFetch / ApiError / normalizeCursor.

import { ApiError, apiFetch, normalizeCursor } from '../../lib/api';

// ---- shapes (FACTS PACK §3c) ----

export interface DeviceOnline {
  state?: string; // 'online' | 'offline' | ...
  stale_hint?: boolean;
}

export interface DeviceRecording {
  mode?: string; // 'continuous' | 'event' | 'off' | ...
  location?: string; // 'cloud' | 'sdcard' | ...
  quality?: string; // '1080p' | ...
}

/** Per-device subscription (only present in Customer-360; omitted in browse). */
export interface DeviceSubscription {
  plan?: string;
  status?: string;
  expired_time?: number; // µs
  auto_renew?: boolean;
  retention_days?: number;
  quality_allowed?: string;
}

export interface AdminDevice {
  device_id: string;
  model?: string;
  display_name?: string;
  firmware_version?: string;
  status?: string; // 'active' | 'disabled' | 'lost' | ...
  online?: DeviceOnline;
  role?: string; // browse: requester relation ('admin'); 360: 'owner' — display as-is
  recording?: DeviceRecording;
  registered_at?: number; // µs
  updated_at?: number; // µs
  subscription?: DeviceSubscription;
  /**
   * Owner's ecosystem/brand family (08 §2.1) — device → owner relation →
   * user.family_id → families.name. Admin browse only; omitted when the device
   * has no owner. owner_family_id like 'fam-platform'; owner_family_name the
   * friendly label ('LS Platform'). Falls back to the id when the families row
   * has no name.
   */
  owner_family_id?: string;
  owner_family_name?: string;
  /** eco-* attribution (common#24/#79, admin browse only). PRODUCT side:
   *  base_ecosystem = device native ecosystem (from its model), product_org =
   *  the model's owner org (brand/manufacturer). joined + platform_union = 加入的.
   *  OWNER side: owner_ecosystem = owner account's native ecosystem. */
  base_ecosystem_id?: string;
  base_ecosystem_name?: string;
  product_org_id?: string;
  product_org_name?: string;
  joined_ecosystems?: { id: string; name: string }[];
  platform_union?: boolean;
  owner_ecosystem_id?: string;
  owner_ecosystem_name?: string;
}

export interface DeviceListParams {
  status?: string;
  model?: string;
  ecosystem?: string;
  limit?: number;
  cursor?: string;
}

export interface DeviceListPage {
  devices: AdminDevice[];
  /** null = last page (covers both "" and an OMITTED next_cursor — FACTS PACK §3c). */
  nextCursor: string | null;
}

/**
 * BROWSE — GET /v3/admin/devices?status=&model=&limit=&cursor=.
 * Returns { data.devices[], data.next_cursor }; next_cursor is OMITTED on the last
 * page, so normalizeCursor() maps missing/"" → null.
 */
export async function listDevices(params: DeviceListParams = {}): Promise<DeviceListPage> {
  const res = await apiFetch<{ devices?: AdminDevice[]; next_cursor?: string }>('/v3/admin/devices', {
    query: {
      status: params.status || undefined,
      model: params.model || undefined,
      ecosystem: params.ecosystem || undefined,
      limit: params.limit,
      cursor: params.cursor || undefined,
    },
  });
  return {
    devices: Array.isArray(res.data?.devices) ? res.data.devices : [],
    nextCursor: normalizeCursor(res.data?.next_cursor),
  };
}

/**
 * Single device by id. The browse endpoint has no by-id GET, but Customer-360 on the
 * device's owner returns the same shape; for the admin device-detail page we re-query
 * the browse list and match (dev has a tiny fleet). Returns null when not found.
 *
 * Detail strategy: try a direct browse with no filter and locate the row. This avoids
 * inventing an endpoint the deployed build doesn't expose. If a future build adds
 * GET /v3/admin/devices/{id} we can swap this out.
 */
export async function getDeviceById(deviceId: string): Promise<AdminDevice | null> {
  // Direct by-id lookup (GET /v3/admin/devices/{id}; single-object envelope).
  // Replaces the old fleet-scan stopgap: 404 is now a REAL absence, never a
  // scan-cap artefact, and detail/deep-links resolve in one call.
  try {
    const res = await apiFetch<AdminDevice>(`/v3/admin/devices/${encodeURIComponent(deviceId)}`);
    return res.data ?? null;
  } catch (e) {
    if (isNotFound(e)) return null;
    throw e;
  }
}

// ---- lifecycle actions (FACTS PACK §3c; body {reason?, case_id?}) ----

export type DeviceAction = 'disable' | 'enable' | 'report-lost';

export interface DeviceActionInput {
  reason?: string;
  caseId?: string;
}

/** Result echo: action endpoints return { data.status } ('disabled' | 'active' | 'lost'). */
export interface DeviceActionResult {
  status?: string;
  requestId: string | null;
}

/**
 * POST /v3/admin/devices/{id}/{action}. Server-side audited (LOCKED rules).
 * Unknown id → 404 NOT_FOUND (verified live). reason/case_id are optional.
 */
export async function deviceAction(deviceId: string, action: DeviceAction, input: DeviceActionInput = {}): Promise<DeviceActionResult> {
  const body: Record<string, string> = {};
  if (input.reason) body.reason = input.reason;
  if (input.caseId) body.case_id = input.caseId;
  const res = await apiFetch<{ status?: string }>(`/v3/admin/devices/${encodeURIComponent(deviceId)}/${action}`, {
    method: 'POST',
    body,
  });
  return { status: res.data?.status, requestId: res.requestId };
}

/** Narrow helper used by detail/list error handling. */
export function isNotFound(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.code === 'NOT_FOUND');
}
